import '../styles/editor.css';
import '../styles/unified-styles.css';

import { EditorContent } from '@tiptap/react';
import React, { useEffect, useRef } from 'react';

import { cn } from '@/ux/utils';

import { useBolderEditor } from '../hooks/useBolderEditor';
import { ContentViewerNavigation } from './ContentViewerNavigation';
import { ScrollProgressBar } from './ScrollProgressBar';

interface ContentViewerProps {
  content: string | Record<string, any>;
  className?: string;
  showProgressBar?: boolean;
  showNavigation?: boolean;
  hideNativeScrollbar?: boolean;
  useViewportScroll?: boolean;
}

/**
 * ContentViewer - A read-only viewer that renders content exactly like the Editor
 *
 * This component uses the same TipTap editor configuration as BolderEditor
 * but in read-only mode, ensuring perfect parity between edit and preview modes.
 */
export const ContentViewer: React.FC<ContentViewerProps> = ({
  content,
  className,
  showProgressBar = true,
  showNavigation = true,
  hideNativeScrollbar = true,
  useViewportScroll = false,
}) => {
  const wrapperRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);

  // Create a read-only editor instance with the same configuration as the main editor
  const editor = useBolderEditor({
    content,
    readOnly: true,
    isPreview: true,
    placeholder: '', // No placeholder in preview mode
  });

  // Force re-render when editor content changes to ensure navigation updates
  useEffect(() => {
    // Small delay to ensure content is rendered
    const timer = setTimeout(() => {
      // Trigger a resize event to help ContentViewerNavigation recalculate
      window.dispatchEvent(new Event('resize'));
    }, 100);

    return () => clearTimeout(timer);
  }, [content, editor]);

  // Handle wheel events on parent containers to ensure scrolling works everywhere
  useEffect(() => {
    if (!wrapperRef.current) return;

    const handleWheel = (e: WheelEvent) => {
      if (!wrapperRef.current) return;

      // Always forward scroll to our wrapper, regardless of where the mouse is
      const target = e.target as Element;
      const isInsideWrapper = wrapperRef.current.contains(target);

      // If scrolling inside wrapper, let it handle naturally
      if (isInsideWrapper) return;

      // Otherwise, forward the scroll event
      const delta = e.deltaY || e.deltaX;
      wrapperRef.current.scrollTop += delta;
    };

    // Find all possible parent containers
    const possibleContainers = [
      document.querySelector('.preview-dialog-fullscreen'),
      document.querySelector('[role="dialog"]'),
      document.querySelector('.FullscreenDialog'),
      document.body,
    ].filter(Boolean);

    // Add listener to all parent containers
    possibleContainers.forEach((container) => {
      if (container) {
        container.addEventListener('wheel', handleWheel, { passive: true });
      }
    });

    return () => {
      possibleContainers.forEach((container) => {
        if (container) {
          container.removeEventListener('wheel', handleWheel);
        }
      });
    };
  }, [editor]); // Re-attach when editor changes

  // Apply exactly the same styles as the editor
  const editorClasses = cn(
    'prose prose-lg mx-auto focus:outline-none',
    'min-h-[200px] p-3 sm:p-5 max-w-none',
    // Responsive typography
    '[&_h1]:text-3xl sm:[&_h1]:text-4xl [&_h1]:font-extrabold [&_h1]:tracking-tight',
    '[&_h2]:text-2xl sm:[&_h2]:text-3xl [&_h2]:font-bold [&_h2]:tracking-tight',
    '[&_h3]:text-xl sm:[&_h3]:text-2xl [&_h3]:font-semibold',
    '[&_p]:text-base [&_p]:leading-relaxed',
    '[&_ul]:list-disc [&_ul]:pl-4 sm:[&_ul]:pl-6',
    '[&_ol]:list-decimal [&_ol]:pl-4 sm:[&_ol]:pl-6',
    '[&_blockquote]:border-l-4 [&_blockquote]:border-border [&_blockquote]:pl-3 sm:[&_blockquote]:pl-4 [&_blockquote]:italic',
    '[&_pre]:bg-muted [&_pre]:rounded-lg [&_pre]:p-3 sm:[&_pre]:p-4 [&_pre]:overflow-x-auto',
    '[&_code]:bg-muted [&_code]:px-1 [&_code]:py-0.5 [&_code]:rounded [&_code]:text-sm',
    '[&_hr]:border-t-2 [&_hr]:border-border [&_hr]:my-6 sm:[&_hr]:my-8',
    // Mobile optimizations
    'touch-manipulation',
    // Better mobile text size to prevent zoom
    'md:text-base text-[16px]',
    // Improve mobile readability
    'selection:bg-primary/20 selection:text-primary-foreground',

    // Hide video links (they're replaced with video players)
    '[&_a[href*="/view?id="]]:hidden',

    // Ensure proper video rendering
    '[&_.video-node-view]:my-6',
    '[&_.video-embed-container]:my-6',

    // Content grid styling
    '[&_.content-grid-container]:my-6',

    // Remove margins for videos inside content grids
    '[&_.content-grid-cell_.video-node-view]:my-0',
    '[&_.content-grid-cell_.video-embed-container]:my-0',
    '[&_.content-grid-container_.video-node-view]:my-0',
    '[&_.content-grid-container_.video-embed-container]:my-0',
    '[&_.content-grid-cell_video]:!my-0',
    '[&_.content-grid-container_video]:!my-0',

    // ProseMirror specific
    '[&_.ProseMirror]:outline-none',
    '[&_.ProseMirror]:min-h-0', // No minimum height in preview

    // Disable all editing UI
    '[&_.ProseMirror-selectednode]:outline-none',
    '[&_.ProseMirror-gapcursor]:hidden',

    // Custom classes
    className,
  );

  if (useViewportScroll) {
    // For viewport scrolling (window scroll), don't create a scrollable container
    return (
      <div className={cn('bolder-editor relative', className)}>
        {showProgressBar && <ScrollProgressBar />}
        {showNavigation && <ContentViewerNavigation contentRef={contentRef} useViewport={true} />}
        <div ref={contentRef}>
          <EditorContent editor={editor} className={cn('w-full', editorClasses)} />
        </div>
      </div>
    );
  }

  // Default behavior with its own scroll container
  return (
    <div className={cn('bolder-editor h-full relative flex flex-col', className)}>
      {hideNativeScrollbar && (
        <style jsx>{`
          .content-viewer-wrapper-no-scrollbar::-webkit-scrollbar {
            display: none;
            width: 0;
            height: 0;
          }
        `}</style>
      )}
      {showProgressBar && <ScrollProgressBar containerRef={wrapperRef} />}
      {showNavigation && <ContentViewerNavigation containerRef={wrapperRef} contentRef={contentRef} />}
      <div
        ref={wrapperRef}
        className={cn(
          'content-viewer-wrapper overflow-y-auto flex-1',
          hideNativeScrollbar && 'content-viewer-wrapper-no-scrollbar scrollbar-hide',
        )}
        style={{
          position: 'relative',
          ...(hideNativeScrollbar && {
            scrollbarWidth: 'none',
            msOverflowStyle: 'none',
          }),
        }}
      >
        <div ref={contentRef} className="min-h-full">
          <EditorContent
            editor={editor}
            className={cn(
              'w-full',
              // Scroll performance optimizations
              'scroll-smooth overscroll-contain',
              editorClasses,
            )}
          />
        </div>
      </div>
    </div>
  );
};
